import { useEffect, useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { platform } from '@core/platform';
import { useRuntimeTheme } from '@core/hooks/useRuntimeTheme';
import { resolveThemeColors } from '@core/utils/themeColors';
import { themes } from '@themes';
import { useIndustry } from './industry-context.js';

export default function ThemeProvider({ children }) {
  const { key } = useIndustry();
  const { primaryColor, secondaryColor, backgroundColor, textColor } = useRuntimeTheme();

  useEffect(() => {
    document.documentElement.dataset.industry = key;
  }, [key]);

  // Activates the safe-area rules in styles/base/_safe-area.scss. Stays 'web'
  // in the browser, where those rules resolve to zero insets. See ADR 0004.
  useEffect(() => {
    document.documentElement.dataset.platform = platform();
  }, []);

  const baseTheme = themes[key] ?? themes.default;
  const theme = useMemo(() => {
    const resolved = resolveThemeColors({
      primaryColor,
      secondaryColor,
      backgroundColor,
      textColor,
    });
    if (!resolved.primary && !resolved.background && !resolved.text && !resolved.secondary) {
      return baseTheme;
    }

    return {
      ...baseTheme,
      token: {
        ...baseTheme.token,
        ...(resolved.primary
          ? {
              colorPrimary: resolved.primary,
              colorInfo: resolved.primary,
              colorLink: resolved.primary,
            }
          : {}),
        ...(resolved.primaryHover
          ? {
              colorPrimaryHover: resolved.primaryHover,
              colorLinkHover: resolved.primaryHover,
              colorPrimaryActive: resolved.primaryActive || resolved.primaryHover,
            }
          : {}),
        ...(resolved.text
          ? {
              colorText: resolved.text,
              colorTextHeading: resolved.text,
              colorTextBase: resolved.text,
            }
          : {}),
        ...(resolved.background
          ? {
              colorBgBase: resolved.background,
              colorBgContainer: resolved.cardBackground || resolved.background,
              colorBgElevated: resolved.cardBackground || resolved.background,
            }
          : {}),
        ...(resolved.border
          ? {
              colorBorder: resolved.border,
              colorBorderSecondary: resolved.inputBorder || resolved.border,
            }
          : {}),
      },
    };
  }, [baseTheme, primaryColor, secondaryColor, backgroundColor, textColor]);

  return <ConfigProvider theme={theme}>{children}</ConfigProvider>;
}
